import { useEffect, useState, type FormEvent } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { FormField } from '../shared/form';
import { CatalogDialog, PRIMARY_BUTTON, SECONDARY_BUTTON } from '../modelcatalog/CatalogDialog';
import UpgradePrompt from '../ui/UpgradePrompt';
import { useSaveAccountProviderKey } from '../../hooks/useAccountModels';
import { extractEntitlementError, isFeatureNotEntitledError } from '../../types/billing';
import type { FeatureNotEntitledError } from '../../types/billing';
import type { AccountModelProvider } from '../../types/accountModels';
import { refusalReason } from '../../utils/apiRefusal';

/**
 * Use your own key / Replace (task 1147, step 3).
 *
 * One masked field. Submitting sends the key to the service, which verifies it
 * by asking the Model Provider for its model list and stores it only when that
 * passes (`POST` to add, `PUT` to replace). A refusal keeps the dialog open
 * with the service's own reason, and nothing was saved. The key never comes
 * back: the provider row shows only its last four characters.
 *
 * Storing a key is a plan capability (`byok`). A plan without it answers 403
 * `feature_not_entitled`; the dialog then shows the shared upgrade prompt
 * rather than restating the reason, as the API Keys page does.
 */
interface ProviderKeyDialogProps {
  provider: AccountModelProvider | null;
  replace: boolean;
  onClose: () => void;
  onSaved: (provider: AccountModelProvider) => void;
}

export function ProviderKeyDialog({ provider, replace, onClose, onSaved }: ProviderKeyDialogProps) {
  const save = useSaveAccountProviderKey();
  const [key, setKey] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [entitlement, setEntitlement] = useState<FeatureNotEntitledError | null>(null);

  useEffect(() => {
    if (!provider) return;
    setKey('');
    setFieldError(undefined);
    setRefusal(null);
    setEntitlement(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id, replace]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!provider) return;
    const trimmed = key.trim();
    if (!trimmed) {
      setFieldError('Paste the API key from your provider account.');
      return;
    }
    setFieldError(undefined);
    setRefusal(null);
    setEntitlement(null);
    try {
      await save.mutateAsync({ providerId: provider.id, key: trimmed, replace });
      setKey('');
      onSaved(provider);
    } catch (error) {
      const entitlementError = extractEntitlementError(error);
      if (entitlementError && isFeatureNotEntitledError(entitlementError)) {
        setEntitlement(entitlementError);
        return;
      }
      setRefusal(refusalReason(error));
    }
  };

  const name = provider?.displayName ?? 'provider';

  return (
    <CatalogDialog
      open={provider !== null}
      onClose={onClose}
      busy={save.isPending}
      title={replace ? `Replace your ${name} key` : `Use your own ${name} key`}
      description={
        replace
          ? `Once you replace the key, all of this account's AI Services use the new key for ${name}.`
          : `Once you add a key, all of this account's AI Services use it for ${name}.`
      }
      testId="provider-key-dialog"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <FormField
            label="API key"
            name="account-provider-key"
            type="password"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              if (fieldError) setFieldError(undefined);
            }}
            error={fieldError}
            required
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Stored encrypted. Shown again only as its last four characters.
          </p>
        </div>

        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          <b>What changes:</b> {name} bills you directly for the tokens your AI Services use on this key, and they
          no longer appear on your Innovate invoice. This applies to every run of every AI Service in this account,
          including runs from an interface, the service page and tests. If the key stops working, those runs fail
          until you replace or remove it.
        </div>

        {refusal && (
          <p role="alert" data-testid="provider-key-error" className="text-sm text-red-600 dark:text-red-400">
            {refusal} Nothing was saved.
          </p>
        )}

        {entitlement && (
          <div data-testid="provider-key-upgrade-prompt">
            <UpgradePrompt error={entitlement} onDismiss={() => setEntitlement(null)} />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} disabled={save.isPending} className={SECONDARY_BUTTON}>
            Cancel
          </button>
          <button type="submit" disabled={save.isPending} className={PRIMARY_BUTTON}>
            {save.isPending ? (
              <>
                <ArrowPathIcon className="-ml-0.5 mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                Verifying…
              </>
            ) : (
              'Verify & save'
            )}
          </button>
        </div>
      </form>
    </CatalogDialog>
  );
}
